/**
 * Verwaltung → Mandant (Fund 10, Jörg 2026-09-28: „Dafür müssten wir die Adresse
 * der eigenen Firma in der Portalseite eintragen können. Auf diese Seite sollte
 * auch das Impressum gehören").
 *
 * Drei Abschnitte (Regel „Dokument-Navigation" E10, Abhakliste 1.46: jeder Abschnitt
 * zeigt zuerst an, „Bearbeiten" öffnet, „Speichern" und „Abbrechen" führen zurück in
 * die Anzeige — nichts speichert still):
 *  1. Firmenstamm — Name, Anschrift, Kontakt, Koordinate („Aus Adresse" über die
 *     lokale Adress-Datenbank). Die Koordinate ist der Firmenstandort für die
 *     Fahrtzeit-Schätzung im Timesheet und den Firmenradius im Tagesabgleich.
 *  2. Fahrt-Schätzung — Umwegfaktor, Tempo Land/Stadt, Rundung.
 *  3. Impressum und Datenschutz — Modus aus der ENV (onvice = Betreiber-Impressum,
 *     tenant = eigener Text hier, aus = geschlossenes System), Link auf die
 *     Datenschutzhinweise für Beschäftigte.
 * Speicherort: portal_tenants.settings (company, fahrt, impressum_text, datenschutz_url).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Loader2, RefreshCw } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';
import { SettingsSection } from '@onvice/portal-ui';

interface Company { name: string; strasse: string; hausnummer: string; plz: string; ort: string; telefon: string; email: string; lat: string; lng: string }
interface Fahrt { umwegfaktor: string; tempo_land_kmh: string; tempo_stadt_kmh: string; rundung_min: string }
interface Impressum { impressumText: string; datenschutzUrl: string }
type ImpressumMode = 'onvice' | 'tenant' | 'aus';

const emptyCompany: Company = { name: '', strasse: '', hausnummer: '', plz: '', ort: '', telefon: '', email: '', lat: '', lng: '' };
const defaultFahrt: Fahrt = { umwegfaktor: '1.3', tempo_land_kmh: '45', tempo_stadt_kmh: '30', rundung_min: '5' };

const MODE_LABEL: Record<ImpressumMode, string> = {
  onvice: 'Betreiber-Impressum von onvice (feste Angaben, Hetzner-Instanz)',
  tenant: 'Eigenes Impressum dieser Instanz (Text unten)',
  aus: 'Geschlossenes System (nur Firmennetz/VPN) — kein Impressum nötig, Seite und Link sind ausgeblendet',
};

const INPUT = 'w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm';
const LABEL = 'block text-xs text-slate-500 mb-1';

function num(v: string): number { return Number(v.trim().replace(',', '.')); }

function parseCoord(company: Company): { lat: number | null; lng: number | null; error: string | null } {
  const lat = company.lat.trim() ? num(company.lat) : null;
  const lng = company.lng.trim() ? num(company.lng) : null;
  if ((lat === null) !== (lng === null) || (lat !== null && !Number.isFinite(lat)) || (lng !== null && !Number.isFinite(lng))) {
    return { lat: null, lng: null, error: 'Koordinate: Breite und Länge gemeinsam als Zahlen angeben.' };
  }
  return { lat, lng, error: null };
}

/**
 * `embedded`: als Abschnitt im Adressbuch-Reiter „Portal-Einstellungen" (Jörg 2026-09-28:
 * „die Portalseite wird über das Adressbuch erreicht" — dort gehört die Mandant-weite
 * Konfiguration hin, Entscheid vom 17.07.). Ohne Prop: eigene Seite /admin/mandant (Tiefenlink).
 */
export default function AdminMandantPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [company, setCompany] = useState<Company | null>(null);
  const [fahrt, setFahrt] = useState<Fahrt | null>(null);
  const [impressum, setImpressum] = useState<Impressum | null>(null);
  const [mode, setMode] = useState<ImpressumMode>('onvice');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const r = await api.get<{ success: boolean; data: Record<string, unknown> }>('/portal/tenant-settings');
      const d = r.data ?? {};
      const c = (d['company'] ?? {}) as Partial<Record<keyof Company, string | number | null>>;
      setCompany({
        name: str(c.name), strasse: str(c.strasse), hausnummer: str(c.hausnummer), plz: str(c.plz), ort: str(c.ort),
        telefon: str(c.telefon), email: str(c.email), lat: str(c.lat), lng: str(c.lng),
      });
      const f = (d['fahrt'] ?? {}) as Partial<Record<keyof Fahrt, number>>;
      setFahrt({
        umwegfaktor: f.umwegfaktor != null ? String(f.umwegfaktor) : defaultFahrt.umwegfaktor,
        tempo_land_kmh: f.tempo_land_kmh != null ? String(f.tempo_land_kmh) : defaultFahrt.tempo_land_kmh,
        tempo_stadt_kmh: f.tempo_stadt_kmh != null ? String(f.tempo_stadt_kmh) : defaultFahrt.tempo_stadt_kmh,
        rundung_min: f.rundung_min != null ? String(f.rundung_min) : defaultFahrt.rundung_min,
      });
      setImpressum({
        impressumText: typeof d['impressum_text'] === 'string' ? (d['impressum_text'] as string) : '',
        datenschutzUrl: typeof d['datenschutz_url'] === 'string' ? (d['datenschutz_url'] as string) : '',
      });
      const m = d['impressum_mode'];
      setMode(m === 'tenant' || m === 'aus' ? m : 'onvice');
    } catch (e) {
      setLoadError((e as Error).message || 'Mandant-Einstellungen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const saveCompany = useCallback(async (value: Company): Promise<Company> => {
    const coord = parseCoord(value);
    if (coord.error) throw new Error(coord.error);
    await api.put('/portal/admin/tenant-settings', {
      company: {
        name: value.name, strasse: value.strasse, hausnummer: value.hausnummer, plz: value.plz, ort: value.ort,
        telefon: value.telefon, email: value.email, lat: coord.lat, lng: coord.lng,
      },
    });
    return value;
  }, []);

  const saveFahrt = useCallback(async (value: Fahrt): Promise<Fahrt> => {
    await api.put('/portal/admin/tenant-settings', {
      fahrt: { umwegfaktor: num(value.umwegfaktor), tempo_land_kmh: num(value.tempo_land_kmh), tempo_stadt_kmh: num(value.tempo_stadt_kmh), rundung_min: num(value.rundung_min) },
    });
    return value;
  }, []);

  const saveImpressum = useCallback(async (value: Impressum, changed: Array<keyof Impressum>): Promise<Impressum> => {
    // Nur geänderte Werte schreiben (E10).
    const body: Record<string, unknown> = {};
    if (changed.includes('impressumText')) body.impressum_text = value.impressumText.trim() || null;
    if (changed.includes('datenschutzUrl')) body.datenschutz_url = value.datenschutzUrl.trim() || null;
    if (Object.keys(body).length > 0) await api.put('/portal/admin/tenant-settings', body);
    return value;
  }, []);

  if (loading) return <div className="p-6 text-slate-400 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Lädt …</div>;

  return (
    <div className={embedded ? 'mt-6 space-y-6' : 'p-4 sm:p-6 max-w-3xl mx-auto space-y-6'} data-test-anchor="mandant">
      <div>
        {embedded
          ? <h2 className="text-lg font-semibold text-slate-100 inline-flex items-center gap-2"><Building2 size={18} /> Mandant</h2>
          : <h1 className="text-xl font-semibold text-slate-100 inline-flex items-center gap-2"><Building2 size={20} /> Mandant</h1>}
        <p className="text-sm text-slate-400 mt-1">Firmenstamm, Fahrtzeit-Schätzung und Impressum dieser Portal-Instanz.</p>
      </div>

      {loadError && (
        <div className="rounded px-3 py-2 text-sm bg-rose-900/40 text-rose-200 flex flex-wrap items-center gap-3" data-test-anchor="mandant-ladefehler">
          <span>Nicht geladen: {loadError}</span>
          <button type="button" onClick={() => void load()} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-rose-800/60 hover:bg-rose-700/60 text-rose-100 text-xs">
            <RefreshCw size={12} /> Erneut laden
          </button>
        </div>
      )}

      <SettingsSection<Company>
        id="mandant:firmenstamm"
        title="Firmenstamm"
        description="Die Koordinate ist der Firmenstandort für die Fahrtzeit-Schätzung im Timesheet."
        baseline={company}
        testAnchor="firmenstamm"
        validate={(v) => { const c = parseCoord(v); return c.error ? [c.error] : []; }}
        onSave={saveCompany}
        renderRead={(v) => <CompanyRead value={v} />}
        renderEdit={(v, setField) => <CompanyEdit value={v} setField={setField} />}
      />

      <SettingsSection<Fahrt>
        id="mandant:fahrt"
        title="Fahrtzeit-Schätzung"
        description="Greift im Timesheet bei „+ Fahrt“, wenn am Kunden kein Stammwert steht: Luftlinie Firma → Kunde × Umwegfaktor ÷ Tempo. Unter 5 km Luftlinie gilt das Stadt-Tempo. Der Stammwert am Kunden schlägt die Schätzung immer."
        baseline={fahrt}
        testAnchor="fahrt"
        validate={(v) => {
          const issues: string[] = [];
          if (!Number.isFinite(num(v.umwegfaktor)) || num(v.umwegfaktor) <= 0) issues.push('Umwegfaktor muss eine Zahl größer 0 sein.');
          if (!Number.isFinite(num(v.tempo_land_kmh)) || num(v.tempo_land_kmh) <= 0) issues.push('Tempo Land muss eine Zahl größer 0 sein.');
          if (!Number.isFinite(num(v.tempo_stadt_kmh)) || num(v.tempo_stadt_kmh) <= 0) issues.push('Tempo Stadt muss eine Zahl größer 0 sein.');
          if (!Number.isFinite(num(v.rundung_min)) || num(v.rundung_min) < 0) issues.push('Rundung muss eine Zahl ab 0 sein.');
          return issues;
        }}
        onSave={saveFahrt}
        renderRead={(v) => (
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <ReadField label="Umwegfaktor" value={v.umwegfaktor} />
            <ReadField label="Tempo Land (km/h)" value={v.tempo_land_kmh} />
            <ReadField label="Tempo Stadt (km/h)" value={v.tempo_stadt_kmh} />
            <ReadField label="Rundung (Minuten)" value={v.rundung_min} />
          </dl>
        )}
        renderEdit={(v, setField) => (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><label className={LABEL}>Umwegfaktor</label><input className={INPUT} value={v.umwegfaktor} onChange={(e) => setField('umwegfaktor', e.target.value)} inputMode="decimal" /></div>
            <div><label className={LABEL}>Tempo Land (km/h)</label><input className={INPUT} value={v.tempo_land_kmh} onChange={(e) => setField('tempo_land_kmh', e.target.value)} inputMode="numeric" /></div>
            <div><label className={LABEL}>Tempo Stadt (km/h)</label><input className={INPUT} value={v.tempo_stadt_kmh} onChange={(e) => setField('tempo_stadt_kmh', e.target.value)} inputMode="numeric" /></div>
            <div><label className={LABEL}>Rundung (Minuten)</label><input className={INPUT} value={v.rundung_min} onChange={(e) => setField('rundung_min', e.target.value)} inputMode="numeric" /></div>
          </div>
        )}
      />

      <SettingsSection<Impressum>
        id="mandant:impressum"
        title="Impressum und Datenschutz"
        baseline={impressum}
        testAnchor="impressum"
        validate={(v) => {
          const u = v.datenschutzUrl.trim();
          return u && !(u.startsWith('https://') || u.startsWith('/')) ? ['Der Link auf die Datenschutzhinweise muss mit https:// oder / beginnen.'] : [];
        }}
        onSave={saveImpressum}
        renderRead={(v) => (
          <div className="space-y-3 text-sm">
            <ModeInfo mode={mode} />
            {mode === 'tenant' && (
              <div>
                <div className={LABEL}>Impressum-Text (Pflichtangaben nach § 5 DDG)</div>
                {v.impressumText.trim()
                  ? <pre className="whitespace-pre-wrap font-sans text-slate-200">{v.impressumText}</pre>
                  : <span className="text-slate-500 italic">Noch kein Text hinterlegt.</span>}
              </div>
            )}
            <div>
              <div className={LABEL}>Link auf die Datenschutzhinweise für Beschäftigte</div>
              {v.datenschutzUrl.trim()
                ? <a href={v.datenschutzUrl} target="_blank" rel="noreferrer" className="text-brand-400 hover:underline break-all">{v.datenschutzUrl}</a>
                : <span className="text-slate-500 italic">Kein Link hinterlegt.</span>}
            </div>
          </div>
        )}
        renderEdit={(v, setField) => (
          <div className="space-y-3">
            <ModeInfo mode={mode} />
            {mode === 'tenant' && (
              <div>
                <label className={LABEL}>Impressum-Text (Pflichtangaben nach § 5 DDG)</label>
                <textarea className={`${INPUT} min-h-[160px]`} value={v.impressumText} onChange={(e) => setField('impressumText', e.target.value)} />
              </div>
            )}
            <div>
              <label className={LABEL}>Link auf die Datenschutzhinweise für Beschäftigte (optional, https:// oder Portal-Pfad)</label>
              <input className={INPUT} value={v.datenschutzUrl} onChange={(e) => setField('datenschutzUrl', e.target.value)} placeholder="https://…/datenschutz-beschaeftigte.pdf" />
              <p className="text-xs text-slate-500 mt-1">Verantwortlich für die Mitarbeiterdaten ist der Arbeitgeber. Der Link erscheint auf der Impressum-Seite.</p>
            </div>
          </div>
        )}
      />
    </div>
  );
}

function ModeInfo({ mode }: { mode: ImpressumMode }) {
  return (
    <div className="text-sm text-slate-300">
      <span className="text-slate-500">Modus dieser Instanz:</span> {MODE_LABEL[mode]}
      {mode !== 'aus' && <> — <Link to="/impressum" className="underline" target="_blank">Seite ansehen</Link></>}
      <p className="text-xs text-slate-500 mt-1">Der Modus wird je Instanz beim Ausrollen gesetzt (Hetzner: onvice · On-Prem: aus oder eigener Text), nicht hier.</p>
    </div>
  );
}

function ReadField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-slate-200">{value.trim() ? value : <span className="text-slate-500">—</span>}</dd>
    </div>
  );
}

function CompanyRead({ value: v }: { value: Company }) {
  const address = [[v.strasse, v.hausnummer].filter((s) => s.trim()).join(' '), [v.plz, v.ort].filter((s) => s.trim()).join(' ')].filter(Boolean).join(', ');
  const coord = v.lat.trim() && v.lng.trim() ? `${v.lat}, ${v.lng}` : '';
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
      <div className="sm:col-span-2"><ReadField label="Firmenname" value={v.name} /></div>
      <ReadField label="Anschrift" value={address} />
      <ReadField label="Koordinate (Breite, Länge)" value={coord} />
      <ReadField label="Telefon" value={v.telefon} />
      <ReadField label="E-Mail" value={v.email} />
    </dl>
  );
}

function CompanyEdit({ value: v, setField }: { value: Company; setField: <K extends keyof Company>(key: K, val: Company[K]) => void }) {
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoInfo, setGeoInfo] = useState<string | null>(null);

  async function geocodeFromAddress() {
    setGeoBusy(true); setGeoInfo(null);
    try {
      const street = [v.strasse.trim(), v.hausnummer.trim()].filter(Boolean).join(' ');
      const place = [v.plz.trim(), v.ort.trim()].filter(Boolean).join(' ');
      if (!street || !place) { setGeoInfo('Bitte zuerst Straße und PLZ/Ort eintragen.'); return; }
      const r = await api.get<{ success: boolean; data: { lat: number; lng: number; displayName: string; source: string } | null }>(
        `/adressbuch/contacts/geocode?q=${encodeURIComponent(`${street}, ${place}`)}`,
      );
      if (!r.data) { setGeoInfo('Adresse nicht gefunden — Schreibweise prüfen oder Koordinate von Hand eintragen.'); return; }
      setField('lat', r.data.lat.toFixed(6)); setField('lng', r.data.lng.toFixed(6));
      setGeoInfo(r.data.source === 'exact' ? `Gefunden: ${r.data.displayName}` : `Ähnliche Adresse gefunden: ${r.data.displayName} — bitte prüfen`);
    } catch (e: unknown) {
      const m = (e as Error).message;
      setGeoInfo(/503|nicht verf/i.test(m) ? 'Adress-Datenbank auf dieser Instanz nicht verfügbar' : m);
    } finally { setGeoBusy(false); }
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2"><label className={LABEL}>Firmenname</label><input className={INPUT} value={v.name} onChange={(e) => setField('name', e.target.value)} /></div>
        <div><label className={LABEL}>Straße</label><input className={INPUT} value={v.strasse} onChange={(e) => setField('strasse', e.target.value)} /></div>
        <div><label className={LABEL}>Hausnummer</label><input className={INPUT} value={v.hausnummer} onChange={(e) => setField('hausnummer', e.target.value)} /></div>
        <div><label className={LABEL}>PLZ</label><input className={INPUT} value={v.plz} onChange={(e) => setField('plz', e.target.value)} inputMode="numeric" /></div>
        <div><label className={LABEL}>Ort</label><input className={INPUT} value={v.ort} onChange={(e) => setField('ort', e.target.value)} /></div>
        <div><label className={LABEL}>Telefon</label><input className={INPUT} value={v.telefon} onChange={(e) => setField('telefon', e.target.value)} /></div>
        <div><label className={LABEL}>E-Mail</label><input className={INPUT} type="email" value={v.email} onChange={(e) => setField('email', e.target.value)} /></div>
        <div><label className={LABEL}>Breite (lat)</label><input className={INPUT} value={v.lat} onChange={(e) => setField('lat', e.target.value)} placeholder="51.850241" inputMode="decimal" /></div>
        <div><label className={LABEL}>Länge (lng)</label><input className={INPUT} value={v.lng} onChange={(e) => setField('lng', e.target.value)} placeholder="7.205358" inputMode="decimal" /></div>
      </div>
      <div className="flex flex-wrap items-center gap-2" data-test-anchor="gps">
        <button type="button" onClick={() => void geocodeFromAddress()} disabled={geoBusy || !v.strasse.trim()} className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 text-sm disabled:opacity-40 min-h-[44px]">
          {geoBusy ? 'Suche …' : 'Aus Adresse'}
        </button>
        <span className="text-xs text-slate-500">{geoInfo ?? 'Die Koordinate ist der Firmenstandort für die Fahrtzeit-Schätzung im Timesheet.'}</span>
      </div>
    </div>
  );
}

function str(v: unknown): string {
  if (v == null) return '';
  return typeof v === 'number' ? String(v) : String(v);
}
